import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  ArrowLeft,
  Upload,
  FileText,
  Download,
  ClipboardList,
  Plus,
  Archive,
  Loader2,
  Clock,
  Search,
  CheckCircle2,
  Ban,
  PackageOpen,
  Play,
  Wrench,
  Users,
} from "lucide-react";
import { maintenanceService } from "../../services/maintenance/planMaestroService";
import { ActividadesPostergadasSelector } from "./ActividadesPostergadasSelector";
import { CloseOTModal } from "./CloseOTModal";
import { toast } from "react-hot-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../ui/dialog";
import { ProveedorSelector } from "./ProveedorSelector";
import { ItemActividadRow } from "./ItemActividadRow";
import { ProveedorMasivoSelector } from "./ProveedorMasivoSelector";
import { OTReportModal } from "./OTReportModal";

interface DetalleOrdenProps {
  otId: number;
  onBack: () => void;
  onUpdate?: (updatedData: { id: number; estado: string }) => void;
}

function DetalleOrden({ otId, onBack, onUpdate }: DetalleOrdenProps) {
  const [ot, setOt] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [repuestosPendientes, setRepuestosPendientes] = useState<any[]>([]);
  const [loadingRepuestos, setLoadingRepuestos] = useState(false);
  const [startingOT, setStartingOT] = useState(false);

  const [modalActividadesPostergadas, setModalActividadesPostergadas] = useState(false);
  // Estados para cierre de OT
  const [modalCierreOpen, setModalCierreOpen] = useState(false);

  // Estado para reporte
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const [isRepuestoModalOpen, setIsRepuestoModalOpen] = useState(false);
  const [itemsCatalogo, setItemsCatalogo] = useState<any[]>([]);
  const [repuestoForm, setRepuestoForm] = useState({
    item_catalogo_id: "",
    cantidad_solicitada: 1,
    proveedor: "",
    notas: "",
  });

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return "-";
    // Avoid "Day Before" bug. For dates without time, force 12:00:00 local
    const hasTime = dateStr.includes('T') && dateStr.split('T')[1].length > 0;
    const finalDateStr = hasTime ? dateStr : `${dateStr.split('T')[0]}T12:00:00`;
    const date = new Date(finalDateStr);
    return new Intl.DateTimeFormat("es-PE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const [isStartConfirmOpen, setIsStartConfirmOpen] = useState(false);

  // Estados para inicio de OT
  const [talleresList, setTalleresList] = useState<any[]>([]);
  const [mecanicosList, setMecanicosList] = useState<any[]>([]);
  const [selectedTallerId, setSelectedTallerId] = useState<string>("");
  const [selectedMecanicos, setSelectedMecanicos] = useState<number[]>([]);

  useEffect(() => {
    if (isStartConfirmOpen) {
      maintenanceService.getTalleres().then((res: any) => {
        setTalleresList(res.data || res);
      }).catch(err => console.error("Error fetching talleres:", err));
    } else {
      setSelectedTallerId("");
      setSelectedMecanicos([]);
      setMecanicosList([]);
    }
  }, [isStartConfirmOpen]);

  useEffect(() => {
    if (selectedTallerId) {
      maintenanceService.getMecanicos(Number(selectedTallerId)).then((res: any) => {
        setMecanicosList(res.data || res);
      }).catch(err => console.error("Error fetching mecanicos:", err));
    } else {
      setMecanicosList([]);
    }
  }, [selectedTallerId]);

  // Estado para selección múltiple de ítems
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Estado para agregar items a la OT
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [itemsDisponibles, setItemsDisponibles] = useState<any[]>([]);
  const [nuevoItem, setNuevoItem] = useState({
    item_catalogo_id: "",
    cantidad: 1,
    proveedor_id: null as number | null,
    precio_cotizado: 0,
    rutina: "",
  });

  const [filtro, setFiltro] = useState("");

  const filteredItems = itemsDisponibles.filter(item =>
    item.nombre.toLowerCase().includes(filtro.toLowerCase()) ||
    item.codigo_interno?.toLowerCase().includes(filtro.toLowerCase())
  );

  const rutinasDisponibles = useMemo(() => {
    const rutinas = new Set<string>();
    if (ot?.rutina?.nombre) rutinas.add(ot.rutina.nombre);

    ot?.actividades?.forEach((act: any) => {
      const obs = act.observaciones;
      if (obs && !obs.startsWith("Postergada") && !obs.startsWith("Cancelada")) {
        rutinas.add(obs);
      }
    });

    return Array.from(rutinas);
  }, [ot]);

  const groupedActividades = useMemo(() => {
    if (!ot?.actividades) return {};
    const groups: Record<string, any[]> = {};
    ot.actividades.forEach((act: any) => {
      const obs = act.observaciones || "";
      let key = "General / Otros";

      // Detectar si es nombre de rutina o mensaje de sistema
      if (obs && !obs.startsWith("Postergada") && !obs.startsWith("Cancelada")) {
        key = obs;
      } else if (obs && (obs.startsWith("Postergada") || obs.startsWith("Cancelada"))) {
        key = "Postergados / Cancelados";
      }

      if (!groups[key]) groups[key] = [];
      groups[key].push(act);
    });
    return groups;
  }, [ot]);

  const itemSeleccionado = itemsDisponibles.find(
    (i) => i.id === Number(nuevoItem.item_catalogo_id),
  );

  const todasRealizadas = ot?.actividades?.every((a: any) => a.realizado);

  useEffect(() => {
    const fetchOt = async () => {
      try {
        setLoading(true);
        const response = await maintenanceService.getOrdenTrabajo(otId);
        const otData = response.data || response;
        setOt(otData);

        if (otData?.vehiculo_id) {
          fetchRepuestosPendientes(otData.vehiculo_id);
        }
      } catch (error) {
        console.error("Error fetching OT:", error);
        toast.error("Error al cargar la orden de trabajo");
      } finally {
        setLoading(false);
      }
    };

    if (otId) {
      fetchOt();
    }
  }, [otId]);

  const fetchRepuestosPendientes = async (vehiculoId: number) => {
    try {
      setLoadingRepuestos(true);
      const response: any = await maintenanceService.getRepuestosPendientes({
        vehiculo_id: vehiculoId,
      });
      setRepuestosPendientes(response.data || response);
    } catch (error) {
      console.error("Error fetching repuestos pendientes:", error);
    } finally {
      setLoadingRepuestos(false);
    }
  };

  const handleStartOT = async () => {
    try {
      setStartingOT(true);
      await maintenanceService.startOT(otId, {
        taller_id: selectedTallerId ? Number(selectedTallerId) : undefined,
        mecanicos_ids: selectedMecanicos
      });
      toast.success("Orden de trabajo iniciada" + (selectedTallerId ? " y asignada" : ""));
      setIsStartConfirmOpen(false);

      const response = await maintenanceService.getOrdenTrabajo(otId);
      const otData = response.data || response;
      setOt(otData);
      if (onUpdate) {
        onUpdate({ id: otId, estado: otData.estado });
      }
    } catch (error: any) {
      console.error("Error al iniciar OT:", error);
      toast.error(error.response?.data?.message || "Error al iniciar la orden de trabajo");
    } finally {
      setStartingOT(false);
    }
  };

  const handleOpenAddItemModal = async () => {
    try {
      const response = await maintenanceService.getAvailableItemsForOT(otId);
      setItemsDisponibles(response.data || []);
      setNuevoItem({
        item_catalogo_id: "",
        cantidad: 1,
        proveedor_id: null,
        precio_cotizado: 0,
        rutina: "",
      });
      setIsAddItemModalOpen(true);
    } catch (error) {
      console.error("Error al cargar ítems:", error);
      toast.error("Error al cargar ítems disponibles");
    }
  };

  const handleAddItem = async () => {
    if (!nuevoItem.item_catalogo_id) {
      toast.error("Selecciona un ítem");
      return;
    }
    if (nuevoItem.cantidad <= 0) {
      toast.error("La cantidad debe ser mayor a 0");
      return;
    }

    try {
      await maintenanceService.addItemToOT(otId, {
        item_catalogo_id: Number(nuevoItem.item_catalogo_id),
        cantidad: nuevoItem.cantidad,
        proveedor_seleccionado_id: nuevoItem.proveedor_id,
        precio_cotizado: nuevoItem.precio_cotizado,
        costo_unitario: nuevoItem.precio_cotizado || 0,
        observaciones: nuevoItem.rutina || undefined,
      });

      toast.success("Ítem agregado a la OT");
      setIsAddItemModalOpen(false);

      const response = await maintenanceService.getOrdenTrabajo(otId);
      const otData = response.data || response;
      setOt(otData);
    } catch (error) {
      toast.error("Error al agregar ítem");
      console.error(error);
    }
  };

  const handleProveedorSelect = (proveedorId: number, precio: number) => {
    setNuevoItem({
      ...nuevoItem,
      proveedor_id: proveedorId,
      precio_cotizado: Number(precio),
    });
  };

  const handleOpenRepuestoModal = async () => {
    try {
      const items = await maintenanceService.getAvailableItemsForOT(otId);
      setItemsCatalogo(Array.isArray(items.data) ? items.data : []);
      setRepuestoForm({
        item_catalogo_id: "",
        cantidad_solicitada: 1,
        proveedor: "",
        notas: "",
      });
      setIsRepuestoModalOpen(true);
    } catch (error) {
      console.error("Error al cargar catálogo:", error);
      toast.error("Error al cargar catálogo de ítems");
    }
  };

  const handleSaveRepuesto = async () => {
    if (!repuestoForm.item_catalogo_id) {
      toast.error("Selecciona un ítem");
      return;
    }
    if (repuestoForm.cantidad_solicitada <= 0) {
      toast.error("La cantidad debe ser mayor a 0");
      return;
    }

    try {
      await maintenanceService.createRepuestoPendiente({
        orden_trabajo_id: otId,
        item_catalogo_id: Number(repuestoForm.item_catalogo_id),
        cantidad_solicitada: Number(repuestoForm.cantidad_solicitada),
        proveedor: repuestoForm.proveedor,
        notas: repuestoForm.notas,
        estado: "SOLICITADO",
      });
      toast.success("Solicitud enviada");
      setIsRepuestoModalOpen(false);
      if (ot?.vehiculo_id) {
        fetchRepuestosPendientes(ot.vehiculo_id);
      }
    } catch (error) {
      toast.error("Error al solicitar repuesto");
      console.error(error);
    }
  };

  const handleUpdateRepuestoStatus = async (repuestoId: number, nuevoEstado: string) => {
    try {
      await maintenanceService.updateRepuestoPendiente(repuestoId, { estado: nuevoEstado });
      toast.success(`Repuesto marcado como ${nuevoEstado}`);
      if (ot?.vehiculo_id) {
        fetchRepuestosPendientes(ot.vehiculo_id);
      }
    } catch (error) {
      toast.error("Error al actualizar estado del repuesto");
      console.error(error);
    }
  };

  // ===== FUNCIONES PARA CIERRE DE OT =====

  const handleCerrarOT = () => {
    setModalCierreOpen(true);
  };

  const handleCierreExitoso = () => {
    setModalCierreOpen(false);
    toast.success("Orden de trabajo cerrada exitosamente");
    if (onUpdate) {
      onUpdate({ id: otId, estado: "CERRADA" });
    }
    onBack(); // Volver a la lista de OTs
  };

  const handleToggleSelection = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleSelectAll = (select: boolean) => {
    if (select) {
      const allIds = ot.actividades?.map((a: any) => a.id) || [];
      setSelectedIds(allIds);
    } else {
      setSelectedIds([]);
    }
  };

  // ===== FIN FUNCIONES DE CIERRE =====

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        Cargando detalles de la orden...
      </div>
    );
  }

  if (!ot) {
    return (
      <div className="p-8 text-center text-red-500">
        No se encontró la orden de trabajo.
      </div>
    );
  }

  const getStatusColor = (estado: string) => {
    switch (estado) {
      case "ABIERTA":
        return "text-blue-600 bg-blue-50 border-blue-200";
      case "EN_PROGRESO":
        return "text-orange-600 bg-orange-50 border-orange-200";
      case "CERRADA":
        return "text-green-600 bg-green-50 border-green-200";
      case "CANCELADA":
        return "text-red-600 bg-red-50 border-red-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };



  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={onBack}
            className="h-9 w-9 rounded-full"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-black text-gray-800 flex items-center gap-2">
              {ot.codigo_ot}
              <Badge
                variant="outline"
                className={`${getStatusColor(ot.estado)} font-bold`}
              >
                {ot.estado}
              </Badge>
            </h1>
            <p className="text-sm text-gray-500">
              Detalles y ejecución de mantenimiento
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {ot.estado === "CERRADA" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsReportModalOpen(true)}
              className="border-slate-300 text-slate-700 hover:bg-slate-50 font-bold"
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar / Reporte
            </Button>
          )}

          {ot.estado === "ABIERTA" && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalActividadesPostergadas(true)}
              className="border-blue-300 text-blue-700"
            >
              <Clock className="h-4 w-4 mr-2" />
              Ver Pendientes
            </Button>
          )}

          {ot.estado === "ABIERTA" && (
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all hover:scale-105 active:scale-95"
              onClick={() => setIsStartConfirmOpen(true)}
              disabled={startingOT}
            >
              {startingOT ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Iniciando...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2 fill-current" />
                  Iniciar Trabajo
                </>
              )}
            </Button>
          )}

          {ot.estado === "EN_PROGRESO" && (
            <Button
              className={`${todasRealizadas
                ? "bg-green-600 hover:bg-green-700"
                : "bg-orange-500 hover:bg-orange-600"
                }`}
              onClick={handleCerrarOT}
            >
              {todasRealizadas ? "✓ Cerrar OT" : "Cerrar OT (Pendientes)"}
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="detalles" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="detalles">Detalles y Ejecución</TabsTrigger>
          <TabsTrigger value="repuestos">Repuestos Pendientes</TabsTrigger>
          <TabsTrigger value="evidencias">Evidencias</TabsTrigger>
        </TabsList>

        <TabsContent value="detalles">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Columna Izquierda */}
            <div className="lg:col-span-2 space-y-6">
              {/* Tarjeta de Vehículo */}
              {/* Tarjeta de Información General */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-bold text-gray-700 flex items-center">
                    <FileText className="h-5 w-5 mr-2 text-blue-600" />
                    Información General
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-4">
                    <div className="p-3 bg-white border-l-4 border-blue-500 rounded-r-lg shadow-sm">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                        Vehículo / Placa
                      </span>
                      <span className="text-lg font-black text-slate-900 leading-none">
                        {ot.vehiculo?.placa}
                      </span>
                      <span className="text-xs text-slate-500 block mt-1">
                        {ot.vehiculo?.marca} {ot.vehiculo?.modelo}
                      </span>
                    </div>

                    <div className="p-3 bg-white border-l-4 border-indigo-500 rounded-r-lg shadow-sm">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                        Tipo & Prioridad
                      </span>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <div className={`h-2 w-2 rounded-full ${ot.tipo === 'PREVENTIVO' ? 'bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.5)]' : 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.5)]'}`} />
                          <span className="font-black text-slate-900 uppercase text-sm">
                            {ot.tipo}
                          </span>
                        </div>
                        <Badge variant="outline" className="w-fit text-[10px] h-4 px-1 py-0">
                          {ot.prioridad}
                        </Badge>
                      </div>
                    </div>

                    <div className="p-3 bg-white border-l-4 border-slate-500 rounded-r-lg shadow-sm">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                        Origen / Rutina
                      </span>
                      <span className="font-bold text-slate-700 text-sm block truncate" title={ot.rutina?.nombre || "N/A"}>
                        {ot.rutina?.nombre || "Sin Rutina Específica"}
                      </span>
                      {ot.planMaestro && (
                        <span className="text-[10px] text-slate-400 block mt-0.5 truncate">
                          Plan: {ot.planMaestro.nombre}
                        </span>
                      )}
                    </div>

                    <div className="p-3 bg-white border-l-4 border-emerald-500 rounded-r-lg shadow-sm">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                        Km Inicial
                      </span>
                      <span className="font-bold text-slate-900">
                        {(ot.kilometraje_inicio || 0).toLocaleString()} <span className="text-[10px] text-slate-400">KM</span>
                      </span>
                    </div>

                    <div className="p-3 bg-white border-l-4 border-rose-500 rounded-r-lg shadow-sm">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                        Fecha Programada
                      </span>
                      <span className="font-black text-blue-700">
                        {formatDateTime(ot.fecha_programada).split(' ')[0]}
                      </span>
                    </div>

                    <div className="p-3 bg-white border-l-4 border-amber-500 rounded-r-lg shadow-sm">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                        Fecha Reg. / Creación
                      </span>
                      <span className="font-bold text-slate-700 italic text-sm">
                        {ot.created_at ? formatDateTime(ot.created_at) : "-"}
                      </span>
                    </div>

                    <div className="p-3 bg-white border-l-4 border-cyan-500 rounded-r-lg shadow-sm">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                        Taller Asignado
                      </span>
                      <div className="flex items-center gap-1.5 overflow-hidden">
                        <Wrench className="h-3 w-3 text-cyan-600 shrink-0" />
                        <span className="font-bold text-slate-900 text-sm truncate">
                          {ot.taller?.nombre || "No Asignado"}
                        </span>
                      </div>
                    </div>

                    <div className="p-3 bg-white border-l-4 border-purple-500 rounded-r-lg shadow-sm md:col-span-2">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">
                        Personal Responsable (Mecánicos)
                      </span>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Users className="h-3 w-3 text-purple-600 shrink-0" />
                        {ot.mecanicos && ot.mecanicos.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {ot.mecanicos.map((m: any) => (
                              <Badge key={m.id} variant="outline" className="text-[10px] h-5 bg-purple-50 text-purple-700 border-purple-200">
                                {m.nombre}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 font-medium italic">
                            Ninguno asignado
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {ot.observaciones && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                      <span className="block text-xs font-bold text-slate-400 uppercase mb-1">Observaciones / Detalles</span>
                      <p className="text-slate-700 font-medium leading-relaxed">
                        {ot.observaciones}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Actividades Checklist */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-bold text-gray-700 flex items-center justify-between">
                    <div className="flex items-center">
                      <ClipboardList className="h-5 w-5 mr-2 text-green-600" />
                      Actividades y Repuestos
                    </div>
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-4 w-4 rounded border flex items-center justify-center cursor-pointer transition-all ${selectedIds.length === (ot.actividades?.length || 0) &&
                          (ot.actividades?.length || 0) > 0
                          ? "border-indigo-500 bg-indigo-500"
                          : "border-gray-300 bg-white"
                          }`}
                        onClick={() =>
                          handleSelectAll(
                            selectedIds.length !== (ot.actividades?.length || 0),
                          )
                        }
                      >
                        {selectedIds.length ===
                          (ot.actividades?.length || 0) &&
                          (ot.actividades?.length || 0) > 0 && (
                            <div className="h-1.5 w-1.5 bg-white rounded-full" />
                          )}
                      </div>
                      <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                        {selectedIds.length > 0
                          ? `${selectedIds.length} seleccionados`
                          : `${ot.actividades?.length || 0} ítems`}
                      </span>
                      {(ot.estado === "ABIERTA" ||
                        ot.estado === "EN_PROGRESO") && (
                          <>
                            <ProveedorMasivoSelector
                              otId={otId}
                              actividades={ot.actividades || []}
                              selectedIds={selectedIds}
                              onSuccess={async () => {
                                const response =
                                  await maintenanceService.getOrdenTrabajo(otId);
                                setOt(response.data || response);
                                setSelectedIds([]); // Limpiar selección tras éxito
                              }}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleOpenAddItemModal}
                              className="h-7"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Agregar
                            </Button>
                          </>
                        )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Barra de progreso */}
                  {ot.estado === "EN_PROGRESO" && ot.actividades?.length > 0 && (
                    <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-blue-900">
                          Progreso de Actividades
                        </span>
                        <span className="text-sm font-bold text-blue-600">
                          {
                            ot.actividades.filter((a: any) => a.realizado)
                              .length
                          }{" "}
                          / {ot.actividades.length}
                        </span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{
                            width: `${(ot.actividades.filter((a: any) => a.realizado)
                              .length /
                              ot.actividades.length) *
                              100
                              }%`,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-1">
                    {Object.entries(groupedActividades).map(([grupo, actividadesGrupo]) => (
                      <div key={grupo} className="mb-6 last:mb-0">
                        {/* Mostrar encabezado de grupo si hay más de uno o si tiene nombre específico */}
                        {(Object.keys(groupedActividades).length > 1 || (grupo !== 'General / Otros')) && (
                          <div className="flex items-center gap-2 mb-3 px-1 border-b pb-1 border-slate-100">
                            <div className="h-2 w-2 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.5)] animate-pulse"></div>
                            <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest flex-1">
                              {grupo}
                            </h4>
                            <div className="flex items-center gap-2">
                              {Object.keys(groupedActividades).length > 1 && grupo !== 'Postergados / Cancelados' && (
                                <span className="text-xs font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                                  S/ {actividadesGrupo.reduce((acc, curr) => acc + (Number(curr.precio_cotizado || curr.costo_unitario) * Number(curr.cantidad)), 0).toFixed(2)}
                                </span>
                              )}
                              <Badge variant="secondary" className="text-[10px] h-5">
                                {actividadesGrupo.length} ítems
                              </Badge>
                            </div>
                          </div>
                        )}
                        <div className="space-y-1">
                          {actividadesGrupo.map((act: any) => {
                            let statusBadge = null;
                            let showPrice = true;

                            if (act.observaciones?.startsWith("Postergada")) {
                              statusBadge = <Badge variant="outline" className="text-[10px] bg-yellow-50 text-yellow-700 border-yellow-200">Postergada</Badge>;
                              showPrice = false;
                            } else if (act.observaciones?.startsWith("Cancelada")) {
                              statusBadge = <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">Cancelada</Badge>;
                              showPrice = false;
                            }

                            return (
                              <ItemActividadRow
                                key={act.id}
                                actividad={act}
                                otEstado={ot.estado}
                                isSelected={selectedIds.includes(act.id)}
                                onSelect={() => handleToggleSelection(act.id)}
                                showPrice={showPrice}
                                customBadge={statusBadge}
                                onUpdate={async () => {
                                  const response =
                                    await maintenanceService.getOrdenTrabajo(otId);
                                  setOt(response.data || response);
                                }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    {(!ot.actividades || ot.actividades.length === 0) && (
                      <div className="text-center py-12 bg-gray-50/50 rounded-xl border-2 border-dashed border-gray-100">
                        <Archive className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 font-medium">No hay actividades registradas en esta orden.</p>
                        <p className="text-xs text-gray-400 mb-4">Para un mantenimiento correctivo, comienza agregando repuestos o servicios.</p>
                        <Button
                          onClick={handleOpenAddItemModal}
                          variant="outline"
                          size="sm"
                          className="bg-white"
                        >
                          <Plus className="h-4 w-4 mr-2 text-blue-600" />
                          Agregar Primera Actividad
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Columna Derecha */}
            <div className="space-y-6">
              {/* Resumen de Costos */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-bold text-gray-700">
                    Resumen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Mano de Obra</span>
                      <span className="font-medium">S/ {Number(ot.costo_mano_obra || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Repuestos</span>
                      <span className="font-medium">
                        S/{" "}
                        {ot.estado === "CERRADA"
                          ? Number(ot.costo_repuestos || 0).toFixed(2)
                          : ot.actividades
                            ?.filter((a: any) => {
                              // Si la orden está en progreso, solo sumar lo realizado
                              if (ot.estado === "EN_PROGRESO") {
                                return a.realizado;
                              }
                              // Si no, excluir solo postergados/cancelados
                              return !a.observaciones?.startsWith("Postergada") && !a.observaciones?.startsWith("Cancelada");
                            })
                            .reduce(
                              (acc: number, curr: any) =>
                                acc +
                                (curr.precio_cotizado || curr.costo_unitario) *
                                curr.cantidad,
                              0,
                            )
                            ?.toFixed(2) || "0.00"}
                      </span>
                    </div>
                    <div className="border-t border-dashed pt-2 flex justify-between items-center">
                      <span className="font-bold text-gray-800">
                        Total Estimado
                      </span>
                      <span className="text-lg font-black text-blue-600">
                        S/{" "}
                        {ot.estado === "CERRADA"
                          ? (Number(ot.costo_repuestos || 0) + Number(ot.costo_mano_obra || 0)).toFixed(2)
                          : (
                            Number(ot.costo_mano_obra || 0) +
                            (ot.actividades
                              ?.filter((a: any) => {
                                if (ot.estado === "EN_PROGRESO") {
                                  return a.realizado;
                                }
                                return !a.observaciones?.startsWith("Postergada") && !a.observaciones?.startsWith("Cancelada");
                              })
                              .reduce(
                                (acc: number, curr: any) =>
                                  acc +
                                  (curr.precio_cotizado || curr.costo_unitario) *
                                  curr.cantidad,
                                0,
                              ) || 0)
                          ).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="repuestos">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-lg font-bold text-gray-800">
                    Repuestos Pendientes
                  </CardTitle>
                  <p className="text-sm text-gray-500">
                    Gestión de solicitudes de repuestos para el vehículo{" "}
                    {ot.vehiculo?.placa}
                  </p>
                </div>
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleOpenRepuestoModal}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Solicitar Repuesto
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingRepuestos ? (
                <div className="text-center py-8">Cargando repuestos...</div>
              ) : repuestosPendientes.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                  <Archive className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">
                    No hay repuestos pendientes
                  </p>
                  <p className="text-xs text-gray-400">
                    Crea una solicitud para este vehículo
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {repuestosPendientes.map((rep) => (
                    <div
                      key={rep.id}
                      className={`flex items-center justify-between p-4 bg-white border rounded-xl shadow-sm transition-all hover:shadow-md ${rep.estado === 'RECIBIDO' ? 'border-green-200 bg-green-50/20' :
                        rep.estado === 'CANCELADO' ? 'opacity-60 grayscale' : 'border-gray-100'
                        }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${rep.estado === 'RECIBIDO' ? 'bg-green-100 text-green-600' :
                          rep.estado === 'CANCELADO' ? 'bg-gray-100 text-gray-400' :
                            'bg-blue-100 text-blue-600'
                          }`}>
                          <PackageOpen size={20} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-800 text-base">
                              {rep.item_catalogo?.nombre}
                            </span>
                            <Badge
                              variant="outline"
                              className={`text-[10px] font-black uppercase ${rep.estado === 'RECIBIDO' ? 'bg-green-100 text-green-700 border-green-200' :
                                rep.estado === 'CANCELADO' ? 'bg-gray-100 text-gray-500 border-gray-200' :
                                  'bg-orange-100 text-orange-700 border-orange-200'
                                }`}
                            >
                              {rep.estado}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs font-medium text-gray-500">
                            <span className="flex items-center gap-1">
                              Cant: <span className="text-gray-900 font-bold">{Number(rep.cantidad_solicitada).toFixed(1)}</span>
                            </span>
                            {rep.proveedor && (
                              <span className="flex items-center gap-1">
                                • Prov: <span className="text-blue-600 font-bold">{rep.proveedor}</span>
                              </span>
                            )}
                            <span>• Solicitado: {new Date(rep.fecha_solicitud).toLocaleDateString()}</span>
                          </div>
                          {rep.notas && (
                            <p className="text-[11px] text-gray-400 mt-1 italic leading-tight">
                              "{rep.notas}"
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Acciones de Repuesto (solo si no está cancelado/recibido) */}
                      {rep.estado === 'SOLICITADO' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-[11px] font-bold border-green-200 text-green-700 hover:bg-green-50"
                            onClick={() => handleUpdateRepuestoStatus(rep.id, 'RECIBIDO')}
                          >
                            <CheckCircle2 size={14} className="mr-1.5" />
                            Recibido
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-[11px] font-bold text-gray-400 hover:text-red-600 hover:bg-red-50"
                            onClick={() => {
                              if (window.confirm('¿Deseas cancelar esta solicitud de repuesto?')) {
                                handleUpdateRepuestoStatus(rep.id, 'CANCELADO');
                              }
                            }}
                          >
                            <Ban size={14} className="mr-1.5" />
                            Cancelar
                          </Button>
                        </div>
                      )}

                      {rep.estado === 'RECIBIDO' && (
                        <div className="flex items-center gap-1.5 text-green-600">
                          <CheckCircle2 size={16} />
                          <span className="text-xs font-black uppercase">Entregado</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="evidencias">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-bold text-gray-700 flex items-center justify-between">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-purple-600" />
                  Evidencias y Documentos
                </div>
                <Button variant="outline" size="sm">
                  <Upload className="h-4 w-4 mr-2" />
                  Subir Archivo
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all cursor-pointer">
                <div className="mx-auto h-12 w-12 text-gray-300 mb-2">
                  <Upload className="h-full w-full" />
                </div>
                <p className="text-sm text-gray-500 font-medium">
                  Subir fotos o documentos
                </p>
                <p className="text-xs text-gray-400 max-w-[150px] mx-auto mt-1">
                  Arrastra archivos aquí o haz clic para explorar
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal para Agregar Ítem a la OT */}
      <Dialog open={isAddItemModalOpen} onOpenChange={setIsAddItemModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Agregar Ítem a la Orden de Trabajo</DialogTitle>
            <DialogDescription>
              Selecciona un ítem y su proveedor. Las "opciones" incluyen cotizaciones individuales y paquetes de proveedores.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label className="text-sm font-black text-slate-500 uppercase tracking-wider ml-1">Buscar Ítem / Repuesto</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Escribe el nombre o código del repuesto..."
                  className="pl-10 h-11 border-2 focus:border-blue-400 transition-all font-medium"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                />
              </div>

              <div className="mt-2 border-2 border-slate-100 rounded-xl overflow-hidden bg-slate-50/30">
                <div className="max-h-[220px] overflow-y-auto">
                  {filteredItems.length > 0 ? (
                    filteredItems.map((item: any) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setNuevoItem({
                            ...nuevoItem,
                            item_catalogo_id: item.id.toString(),
                            proveedor_id: null,
                            precio_cotizado: 0,
                          });
                          setFiltro(item.nombre); // Mostrar el nombre seleccionado
                        }}
                        className={`w-full text-left px-4 py-3 text-sm flex items-center justify-between border-b border-slate-100 last:border-0 hover:bg-white transition-colors ${nuevoItem.item_catalogo_id === item.id.toString()
                          ? 'bg-blue-50 border-l-4 border-l-blue-500'
                          : ''
                          }`}
                      >
                        <div className="flex flex-col">
                          <span className="font-bold text-slate-900">{item.nombre}</span>
                          <span className="text-[10px] text-slate-500 uppercase font-black">{item.codigo_interno} • {item.unidad_medida}</span>
                        </div>
                        {item.cotizaciones_disponibles > 0 && (
                          <Badge
                            variant="outline"
                            className="bg-green-50 text-green-700 border-green-200 text-[10px] font-black cursor-help"
                            title={`Este ítem tiene ${item.cotizaciones_disponibles} opciones de compra disponibles, incluyendo cotizaciones individuales y paquetes de proveedores`}
                          >
                            {item.cotizaciones_disponibles} OPCIONES
                          </Badge>
                        )}
                      </button>
                    ))
                  ) : (
                    <div className="p-8 text-center">
                      <p className="text-sm text-slate-400 italic">No se encontraron ítems con ese nombre</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {rutinasDisponibles.length > 0 && (
              <div className="grid gap-2">
                <label className="text-sm font-medium">Asignar a Rutina (Opcional)</label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={nuevoItem.rutina}
                  onChange={(e) =>
                    setNuevoItem({
                      ...nuevoItem,
                      rutina: e.target.value,
                    })
                  }
                >
                  <option value="">-- General / Sin Rutina Específica --</option>
                  {rutinasDisponibles.map((rutina) => (
                    <option key={rutina} value={rutina}>
                      {rutina}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid gap-2">
              <label className="text-sm font-medium">Cantidad</label>
              <Input
                type="number"
                min="1"
                value={nuevoItem.cantidad}
                onChange={(e) =>
                  setNuevoItem({
                    ...nuevoItem,
                    cantidad: Number(e.target.value),
                  })
                }
              />
            </div>

            {nuevoItem.item_catalogo_id && (
              <div className="grid gap-2">
                <label className="text-sm font-medium">Proveedor</label>
                {itemSeleccionado?.cotizaciones_disponibles > 0 ? (
                  <ProveedorSelector
                    itemId={Number(nuevoItem.item_catalogo_id)}
                    itemNombre={itemSeleccionado?.nombre || ""}
                    onSelect={handleProveedorSelect}
                    proveedorPreseleccionado={
                      nuevoItem.proveedor_id || undefined
                    }
                  />
                ) : (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                    No hay proveedores con cotizaciones vigentes para este ítem.
                    <p className="mt-1 text-xs">
                      Precio referencial: S/{" "}
                      {itemSeleccionado?.precio_referencial || 0}
                    </p>
                  </div>
                )}
              </div>
            )}

            {nuevoItem.proveedor_id && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-900">
                  Precio cotizado
                </p>
                <p className="text-2xl font-bold text-blue-600">
                  S/ {Number(nuevoItem.precio_cotizado).toFixed(2)}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  Total: S/{" "}
                  {(
                    Number(nuevoItem.precio_cotizado) * nuevoItem.cantidad
                  ).toFixed(2)}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddItemModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddItem}
              disabled={!nuevoItem.item_catalogo_id}
            >
              Agregar Ítem
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Solicitud de Repuesto */}
      <Dialog open={isRepuestoModalOpen} onOpenChange={setIsRepuestoModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Solicitar Repuesto</DialogTitle>
            <DialogDescription>
              Solicita un ítem del catálogo o externo
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium">Ítem</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={repuestoForm.item_catalogo_id}
                onChange={(e) =>
                  setRepuestoForm({
                    ...repuestoForm,
                    item_catalogo_id: e.target.value,
                  })
                }
              >
                <option value="">Selecciona un ítem</option>
                {itemsCatalogo.map((item: any) => (
                  <option key={item.id} value={item.id}>
                    {item.nombre} - {item.codigo_interno}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <label className="text-sm font-medium">Cantidad</label>
                <Input
                  type="number"
                  min="1"
                  value={repuestoForm.cantidad_solicitada}
                  onChange={(e) =>
                    setRepuestoForm({
                      ...repuestoForm,
                      cantidad_solicitada: Number(e.target.value),
                    })
                  }
                />
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-medium">
                  Proveedor (Opcional)
                </label>
                <Input
                  value={repuestoForm.proveedor}
                  onChange={(e) =>
                    setRepuestoForm({
                      ...repuestoForm,
                      proveedor: e.target.value,
                    })
                  }
                  placeholder="Ej. Importaciones SAC"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium">Notas</label>
              <textarea
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={repuestoForm.notas}
                onChange={(e) =>
                  setRepuestoForm({ ...repuestoForm, notas: e.target.value })
                }
                placeholder="Detalles adicionales..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsRepuestoModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button onClick={handleSaveRepuesto}>Solicitar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>



      {/* Modal de Actividades Postergadas */}
      <ActividadesPostergadasSelector
        open={modalActividadesPostergadas}
        onClose={() => setModalActividadesPostergadas(false)}
        vehiculoId={ot?.vehiculo_id}
        otId={otId}
        onAplicadas={async () => {
          const response = await maintenanceService.getOrdenTrabajo(otId);
          setOt(response.data || response);
          toast.success("Actividades postergadas agregadas");
        }}
      />

      {/* Modal de Cierre de OT */}
      <CloseOTModal
        open={modalCierreOpen}
        onClose={() => setModalCierreOpen(false)}
        ot={ot}
        onSuccess={handleCierreExitoso}
      />

      {/* Modal Confirmación de Inicio de OT */}
      <Dialog open={isStartConfirmOpen} onOpenChange={setIsStartConfirmOpen}>
        <DialogContent className="sm:max-w-[450px] p-0 overflow-hidden border-none shadow-2xl">
          <div className="bg-indigo-600 p-6 flex items-center justify-center">
            <div className="h-20 w-20 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
              <div className="h-14 w-14 bg-white rounded-full flex items-center justify-center shadow-lg">
                <Play className="h-7 w-7 text-indigo-600 fill-current ml-1" />
              </div>
            </div>
          </div>
          <div className="p-6 text-center">
            <h3 className="text-xl font-black text-gray-900 mb-2">
              Iniciar Mantenimiento
            </h3>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              Esta acción marcará el ingreso oficial del vehículo <span className="font-bold text-gray-900">{ot?.vehiculo?.placa}</span> al taller. Por favor asigne el taller y personal responsable.
            </p>

            <div className="space-y-4 mb-6 text-left">
              <div className="grid gap-2">
                <label className="text-sm font-medium text-gray-700">Taller Asignado</label>
                <select
                  className="w-full text-sm h-10 px-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={selectedTallerId}
                  onChange={(e) => {
                    setSelectedTallerId(e.target.value);
                    setSelectedMecanicos([]);
                  }}
                >
                  <option value="">-- Seleccionar Taller --</option>
                  {talleresList.map(t => (
                    <option key={t.id} value={t.id}>{t.nombre} ({t.tipo})</option>
                  ))}
                </select>
              </div>

              {selectedTallerId && (
                <div className="grid gap-2">
                  <label className="text-sm font-medium text-gray-700">Mecánicos Asignados</label>
                  {mecanicosList.length === 0 ? (
                    <p className="text-xs text-orange-600 p-2 bg-orange-50 rounded border border-orange-200">
                      No hay mecánicos registrados en este taller. Registra personal en el módulo de Talleres para continuar.
                    </p>
                  ) : (
                    <div className="max-h-32 overflow-y-auto w-full border border-gray-200 rounded-md bg-gray-50 p-2 space-y-1">
                      {mecanicosList.map(m => (
                        <label key={m.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${selectedMecanicos.includes(m.id) ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-gray-100 bg-white border border-transparent'}`}>
                          <input
                            type="checkbox"
                            checked={selectedMecanicos.includes(m.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMecanicos(prev => [...prev, m.id]);
                              } else {
                                setSelectedMecanicos(prev => prev.filter(id => id !== m.id));
                              }
                            }}
                            className="rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                          />
                          <span className="text-sm text-gray-700">{m.nombre} <span className="text-xs text-gray-400">({m.especialidad || 'General'})</span></span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={() => setIsStartConfirmOpen(false)}
                className="h-11 font-bold border-2"
              >
                No, volver
              </Button>
              <Button
                onClick={handleStartOT}
                disabled={startingOT}
                className="h-11 bg-indigo-600 hover:bg-indigo-700 font-bold shadow-lg shadow-indigo-100"
              >
                {startingOT ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sí, Iniciar Ahora"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {isReportModalOpen && (
        <OTReportModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          ot={ot}
        />
      )}
    </div>
  );
}

export default DetalleOrden;
